"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const uuid_1 = require("uuid");
const bindEvents_1 = __importDefault(require("./bindEvents"));
const constants_1 = require("./constants");
const fetch_1 = require("./fetch");
const logger_1 = __importStar(require("./logger"));
const storage_1 = require("./storage");
const utils_1 = require("./utils");
const deviceDetails_1 = require("./utils/deviceDetails");
const getTimeZone_1 = __importDefault(require("./utils/getTimeZone"));
const isBot_1 = __importDefault(require("./utils/isBot"));
class CueAnalytics {
    options;
    userId;
    userType;
    sessionId = 'DEFAULT';
    guestId = 'DEFAULT';
    events = [];
    batchedEvents;
    lastEventTime = 0;
    logger;
    timer;
    syncInterval;
    sessionTimeout;
    fetchTimeout;
    maxBucketSize;
    systemAttributes = {};
    storage;
    plugins;
    eventNames;
    eventAttributes;
    constructor(options) {
        this.options = options;
        this.storage = options.storage;
        const logLevel = this.options.logLevel || logger_1.LOG_LEVELS.DEBUG;
        this.logger = new logger_1.default(this.options.logEnabled || false, logLevel);
        this.userType = this.options.userType ?? 'UNKNOWN';
        this.batchedEvents = [];
        this.syncInterval = options.syncInterval ?? constants_1.SYNC_INTERVAL;
        this.sessionTimeout = options.sessionTimeout ?? constants_1.SESSION_TIMEOUT;
        this.fetchTimeout = options.fetchTimeout ?? constants_1.FETCH_TIMEOUT;
        this.maxBucketSize = options.maxBucketSize ?? constants_1.MAX_BUCKET_SIZE;
        this.plugins = this.options.plugins;
        this.eventNames = this.options.appId === 'cuemath.com' ? constants_1.EVENTS_V1 : constants_1.EVENTS;
        this.eventAttributes =
            this.options.appId === 'cuemath.com' ? constants_1.EVENT_ATTRIBUTES_V1 : constants_1.EVENT_ATTRIBUTES;
        this.logger.debug('Initialized with options', this.options);
        this.logger.debug('User Id:', this.userId);
        this.logger.debug('User Type:', this.userType);
        this.logger.debug('Session Timeout:', this.sessionTimeout);
        this.logger.debug('Syncing interval:', this.syncInterval);
        (0, storage_1.readFromStorage)({
            storage: this.storage,
            logger: this.logger,
            callback: data => {
                this.setFromStorage(data);
                this.bindEvents();
                this.timer = setTimeout(this.syncEvents, this.syncInterval);
                if (this.plugins && this.plugins.length > 0) {
                    this.plugins.forEach(plugin => {
                        this.logger.debug('Initializing plugin: ', plugin.name);
                        plugin.initialize({
                            logger: this.logger,
                            eventNames: this.eventNames,
                            eventAttributeNames: this.eventAttributes,
                        });
                    });
                }
            },
        });
        this.setSystemAttributes();
    }
    setFromStorage = (data) => {
        this.sessionId = data.sessionId;
        this.guestId = data.guestId;
        this.events = [...this.events, ...data.events];
        this.lastEventTime = data.lastEventTime;
        this.logger.debug('Session Id:', this.sessionId);
        this.logger.debug('Guest Id:', this.guestId);
        this.logger.debug('Stored Events:', this.events);
        this.logger.debug('Last Event time:', this.lastEventTime);
    };
    setSystemAttributes = () => {
        const deviceInformation = (0, deviceDetails_1.getDeviceInformation)();
        const { device_details: { browser_name: browserName = undefined, os_name: osName = undefined, } = {}, } = deviceInformation || {};
        this.systemAttributes = {
            browser_name: browserName,
            os_name: osName,
            timezone: (0, getTimeZone_1.default)(),
        };
    };
    bindEvents = () => {
        (0, bindEvents_1.default)({
            onBlurOrBackground: () => {
                this.logger.debug('Window/App is in background');
                clearTimeout(this.timer);
                this.syncEvents(false);
            },
            onFocusOrActive: () => {
                this.logger.debug('Window/App is in foreground');
                clearTimeout(this.timer);
                this.syncEvents(true);
            },
            onStoreUpdated: () => {
                this.logger.debug('Storage is updated');
                (0, storage_1.readFromStorage)({
                    callback: this.setFromStorage,
                    storage: this.storage,
                    logger: this.logger,
                });
            },
        });
    };
    syncSingleEvent = async (event) => {
        this.logger.debug('Sync Events Called');
        const { environment } = this.options;
        const isDevelopment = environment === 'DEVELOPMENT';
        const payload = {
            sent_ts: Date.now(),
            environment,
            events: [event],
            system_attributes: this.systemAttributes,
        };
        const [requestCompleted, error] = !isDevelopment
            ? await (0, fetch_1.fetchCall)(`${this.options.baseURL || (0, utils_1.getBaseURL)(environment)}${constants_1.EVENTS_API_PATH}`, payload, this.fetchTimeout, this.logger)
            : [true];
        if (requestCompleted) {
            this.logger.debug('Fetch Success');
        }
        else {
            this.trackEvent('cue_analytics_fetch_failed', {
                error,
                event_ids: [event.event_id],
            });
        }
    };
    syncEvents = async (loop = true) => {
        this.logger.debug('Sync Events Called');
        if (this.events.length > 0 && this.batchedEvents.length === 0) {
            this.batchedEvents = [...this.events];
            const { environment } = this.options;
            const isDevelopment = environment === 'DEVELOPMENT';
            const payload = {
                sent_ts: Date.now(),
                environment,
                events: this.batchedEvents,
                system_attributes: this.systemAttributes,
            };
            const [requestCompleted, error] = !isDevelopment
                ? await (0, fetch_1.fetchCall)(`${this.options.baseURL || (0, utils_1.getBaseURL)(environment)}${constants_1.EVENTS_API_PATH}`, payload, this.fetchTimeout, this.logger)
                : [true];
            if (requestCompleted) {
                const batchedEventIds = this.batchedEvents.map(event => event.event_id);
                this.events = this.events.filter(event => !batchedEventIds.includes(event.event_id));
                (0, storage_1.saveToStorage)({
                    sessionId: this.sessionId,
                    guestId: this.guestId,
                    events: this.events,
                    lastEventTime: this.lastEventTime,
                }, this.storage, this.logger);
            }
            else {
                this.trackEvent('cue_analytics_fetch_failed', {
                    status: error,
                    event_ids: this.batchedEvents.map(event => event.event_id),
                });
            }
            this.batchedEvents = [];
            this.logger.debug('Batch events Reset');
        }
        else {
            this.logger.debug(this.events.length === 0
                ? 'No events in the queue to sync'
                : 'Previous fetch request is still in progress');
        }
        if (loop) {
            this.timer = setTimeout(this.syncEvents, this.syncInterval);
            this.logger.debug('SyncEvents timer set');
        }
    };
    updateSession = () => {
        if (Date.now() - this.lastEventTime > this.sessionTimeout) {
            this.sessionId = (0, uuid_1.v4)();
            this.logger.debug('Session expired, creating new session id:', this.sessionId);
        }
    };
    getEvent = ({ eventName, attributes, overrides }) => {
        return {
            category: overrides?.category === 'PERFORMANCE' ? 'PERFORMANCE' : 'PRODUCT',
            app_id: this.options.appId,
            app_version: this.options.appVersion,
            app_build_number: this.options.appBuildNumber,
            session_id: this.sessionId,
            event_id: (0, uuid_1.v4)(),
            event_name: eventName,
            event_ts: Date.now(),
            guest_id: this.guestId,
            user_id: overrides?.userId || this.userId,
            user_type: overrides?.userType || this.userType || 'UNKNOWN',
            attributes: attributes
                ? { ...attributes, device_details: (0, deviceDetails_1.getDeviceInformation)() }
                : attributes,
        };
    };
    saveEvents = (events) => {
        (0, storage_1.saveToStorage)({
            sessionId: this.sessionId,
            guestId: this.guestId,
            events,
            lastEventTime: this.lastEventTime,
        }, this.storage, this.logger);
    };
    isReady = () => this.lastEventTime !== 0;
    getPluginAttributes = (event) => {
        const { attributes } = event;
        return {
            ...attributes,
            cuemath_event_ts: event.event_ts,
            cuemath_sent_ts: Date.now(),
            cuemath_event_id: event.event_id,
            cuemath_guest_id: this.guestId,
            cuemath_device_details: attributes?.device_details,
            cuemath_app_version: this.options.appVersion,
            cuemath_app_build_number: this.options.appBuildNumber,
            cuemath_app_id: this.options.appId,
            device_details: undefined,
        };
    };
    handlePlugins = (event, overrides) => {
        const isPluginEnabled = overrides?.plugginsDisabled !== true;
        if (isPluginEnabled && this.plugins && this.plugins.length > 0) {
            this.plugins.forEach(plugin => {
                const canPluginTrack = plugin.canTrackAllEvents() ||
                    (overrides && overrides[plugin.name] === true);
                if (canPluginTrack) {
                    plugin.trackEvent(event.event_name, this.getPluginAttributes(event));
                }
            });
        }
    };
    trackParsedEvent = (event, overrides) => {
        const isBotUser = (0, isBot_1.default)();
        const immediate = overrides?.immediate;
        if (isBotUser)
            return;
        this.handlePlugins(event, overrides);
        if (immediate) {
            this.syncSingleEvent(event);
            this.logger.log(`Event Received: ${event.event_name}`, event.attributes);
            return;
        }
        this.lastEventTime = event.event_ts;
        this.events.push(event);
        this.logger.log(`Event Received: ${event.event_name}`, event.attributes);
        this.saveEvents(this.events);
        const isBucketFull = this.events.length > this.maxBucketSize;
        if (isBucketFull) {
            clearTimeout(this.timer);
            this.syncEvents();
        }
    };
    trackEvent = (eventName, attributes, overrides) => {
        // Tracking will be skipped only from the cueAnalytics. Plugins sdk can have there own bot logic.
        this.updateSession();
        const event = this.getEvent({
            eventName,
            attributes: { ...attributes, sent_ts: Date.now() },
            overrides,
        });
        this.trackParsedEvent(event, overrides);
    };
    trackScreen = (screenName, attributes, overrides) => {
        const event = this.getEvent({
            eventName: this.eventNames.SCREEN_VIEW,
            attributes: {
                ...attributes,
                [this.eventAttributes.SCREEN_NAME]: screenName,
                sent_ts: Date.now(),
            },
            overrides,
        });
        this.trackParsedEvent(event, {
            ...overrides,
            plugginsDisabled: true,
        });
        if (overrides?.plugginsDisabled !== true && this.plugins && this.plugins.length > 0) {
            this.plugins.forEach(plugin => {
                if (plugin.canTrackAllEvents() ||
                    (overrides && overrides[plugin.name] === true)) {
                    plugin.trackScreen(screenName, this.getPluginAttributes(event));
                }
            });
        }
    };
    trackClick = (CTA, screenName, attributes, overrides) => {
        const event = this.getEvent({
            eventName: this.eventNames.CLICK,
            attributes: {
                ...attributes,
                [this.eventAttributes.CTA]: CTA,
                [this.eventAttributes.SCREEN_NAME]: screenName,
                sent_ts: Date.now(),
            },
            overrides,
        });
        this.trackParsedEvent(event, {
            ...overrides,
            plugginsDisabled: true,
        });
        if (overrides?.plugginsDisabled !== true && this.plugins && this.plugins.length > 0) {
            this.plugins.forEach(plugin => {
                if (plugin.canTrackAllEvents() ||
                    (overrides && overrides[plugin.name] === true)) {
                    plugin.trackClick(CTA, screenName, this.getPluginAttributes(event));
                }
            });
        }
    };
    trackEvents = (events, overrides) => {
        if ((0, isBot_1.default)()) {
            this.logger.log('Skipping Events from bot');
            return;
        }
        this.updateSession();
        ('length' in events ? events : [events]).forEach(({ eventName, attributes }) => {
            const event = this.getEvent({
                eventName,
                attributes,
                overrides,
            });
            this.lastEventTime = event.event_ts;
            this.events.push(event);
            this.logger.debug(`Event Received: ${eventName}`, attributes);
        });
        this.saveEvents(this.events);
        if (overrides?.immediate) {
            clearTimeout(this.timer);
            this.syncEvents();
        }
    };
    trackPerformanceEvents = (eventName, entries) => {
        this.trackEvents(entries.map(entry => ({
            eventName,
            attributes: entry,
        })), {
            category: 'PERFORMANCE',
        });
    };
    login = (userId, userType) => {
        this.userId = userId;
        this.userType = userType;
        this.logger.debug('Setting Login user', this.userId, this.userType);
        if (this.plugins && this.plugins.length > 0) {
            this.plugins.forEach(plugin => {
                if (this.options.appId === 'cuemath.com' && plugin.name === 'mixpanel') {
                    /*
                      login to be handled differently for cuemath.com on mixpanel and cueanalytics
                      CueAnalytics - User will be parent
                      Mixpanel - User will be student
                      In case of cuemath.com, we will call mixpanel.login directly from cuemath.com
                    */
                    return;
                }
                plugin.login(userId, userType);
            });
        }
    };
    logout = (userType) => {
        this.userId = undefined;
        this.userType = userType || this.userType;
        this.guestId = (0, uuid_1.v4)();
        this.logger.debug('Logging out', {
            guestId: this.guestId,
            userId: this.userId,
            userType: this.userType,
        });
        (0, storage_1.saveToStorage)({
            sessionId: this.sessionId,
            guestId: this.guestId,
            events: this.events,
            lastEventTime: this.lastEventTime,
        }, this.storage, this.logger);
    };
    enableLogs() {
        this.logger.enableLogs();
    }
    disableLogs() {
        this.logger.disableLogs();
    }
    getGuestId() {
        return this.guestId;
    }
    getDeviceDetails() {
        return (0, deviceDetails_1.getDeviceInformation)();
    }
}
exports.default = CueAnalytics;
//# sourceMappingURL=analytics.js.map